import type { DateSelectInfo, EventApi } from '@fullcalendar/react';
import { formatIsoDate, subtractDay, toTime } from '@src/shared/lib';
import { getAbsencesIdsForRange, getUserAbsences } from '../lib/absence';
import type { Absence, TimelineSlot } from '../types';

/**
 * Turning what the renderer reports into what the application works with.
 *
 * The two entry points here are the only places FullCalendar's own objects are
 * read. Everything past them is a `TimelineSlot`: dates as written days, times
 * as `HH:MM`, absences as ids. That is the boundary the rest of the feature is
 * built on -- no draft state holds a DOM node or a calendar object, so the
 * renderer can be replaced without any of it moving.
 */

/**
 * The slot a drag over the grid describes.
 *
 * All-day `end` is exclusive, so the last day covered is the day before it.
 */
export function getSlotDatefromSelection(
  info: DateSelectInfo,
  absences: Absence[],
): TimelineSlot {
  const end = info.allDay ? subtractDay(info.end, 1) : info.end;
  const userId = info.resource?.extendedProps.userId;
  const userAbsences = getUserAbsences(userId, absences);
  const startTime = info.allDay ? null : toTime(info.start);
  const endTime = info.allDay ? null : toTime(info.end);
  return {
    resourceId: info?.resource?.id,
    start: formatIsoDate(info.start),
    end: formatIsoDate(end),
    startTime,
    endTime,
    selectedIds: getAbsencesIdsForRange(info.start, end, userAbsences, {
      dayView: !info.allDay,
      startTime,
      endTime,
    }),
    editedAbsenceId: null,
    userId,
  };
}

/**
 * The slot one event describes, or `null` where it describes nothing usable.
 *
 * `getResources()` answers `[]` for an event with no resolvable resource, and
 * that is reachable: `userResourceId` returns `undefined` for a falsy user id.
 * So the resource and the id on it are checked, not just the dates -- a slot
 * without an employee is not a slot.
 */
export function getSlotFromEvent(event: EventApi): TimelineSlot | null {
  if (!event.start) return null;
  if (!event.end) return null;

  const resource = event.getResources()[0];
  const userId = resource?.extendedProps.userId;
  if (
    !resource?.id ||
    typeof userId !== 'number' ||
    !Number.isSafeInteger(userId) ||
    userId <= 0
  ) {
    return null;
  }

  const absenceId = Number(event.extendedProps.absenceId ?? event.id);
  // All-day `end` is exclusive here too. Taking it off the *start* -- which
  // this did -- gives the day before the event begins, and a range whose end
  // precedes its start reads downstream as a fully occupied slot.
  const end = event.allDay ? subtractDay(event.end, 1) : event.end;
  return {
    resourceId: resource.id,
    start: formatIsoDate(event.start),
    end: formatIsoDate(end),
    startTime: event.allDay ? null : toTime(event.start),
    endTime: event.allDay ? null : toTime(event.end),
    selectedIds: [absenceId],
    editedAbsenceId: absenceId,
    userId,
  };
}
